import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  IconButton,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import { useUpdate, useNotify, useDataProvider } from 'react-admin';

interface LocationZoneDialogProps {
  open: boolean;
  onClose: () => void;
  record: any;
}

export const LocationZoneDialog: React.FC<LocationZoneDialogProps> = ({ open, onClose, record }) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  
  const [formData, setFormData] = useState({
    zoneId: record?.zoneId || '',
    customerTypeId: record?.customerTypeId || '',
  });
  
  const [zones, setZones] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Fetch reference data when dialog opens
  useEffect(() => {
    if (open) {
      const fetchReferenceData = async () => {
        setLoadingData(true);
        try {
          const [zonesResponse, customerTypesResponse] = await Promise.all([
            dataProvider.getList('crm/zones', {
              pagination: { page: 1, perPage: 100 },
              sort: { field: 'name', order: 'ASC' },
              filter: {},
            }),
            dataProvider.getList('crm/customer-types', {
              pagination: { page: 1, perPage: 100 },
              sort: { field: 'name', order: 'ASC' },
              filter: {},
            }),
          ]);
          
          setZones(zonesResponse.data || []);
          setCustomerTypes(customerTypesResponse.data || []);
        } catch (error) {
          console.error('Error fetching reference data:', error);
          notify('Error loading form data', { type: 'error' });
        } finally {
          setLoadingData(false);
        }
      };

      fetchReferenceData();
    }
  }, [open, dataProvider, notify]);

  // Update form data when record changes
  useEffect(() => {
    if (record) {
      setFormData({
        zoneId: record.zoneId || '',
        customerTypeId: record.customerTypeId || '',
      });
    }
  }, [record]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await update('customers', {
        id: record.id,
        data: {
          zoneId: formData.zoneId || null,
          customerTypeId: formData.customerTypeId || null,
        },
        previousData: record,
      });
      notify('Location & zone information updated successfully', { type: 'success' });
      onClose();
    } catch (error) {
      notify('Error updating location & zone information', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="primary" />
            <Typography variant="h6">Location & Zone</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              loading={loadingData}
              options={zones}
              getOptionLabel={(option: any) => option.name || ''}
              value={zones.find((zone: any) => zone.id === formData.zoneId) || null}
              onChange={(event, newValue) => handleChange('zoneId', newValue?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Service Zone"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingData && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              loading={loadingData}
              options={customerTypes}
              getOptionLabel={(option: any) => option.name || ''}
              value={customerTypes.find((ct: any) => ct.id === formData.customerTypeId) || null}
              onChange={(event, newValue) => handleChange('customerTypeId', newValue?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer Type"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingData && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};